import { useState } from 'react';
import type { GoalArchetypeId, UserProfile } from '../types';
import { suggestArchetypeFromBaseline } from '../lib/goals';
import BaselineSummaryStep from './BaselineSummaryStep';
import GoalArchetypePicker from './GoalArchetypePicker';

export type OnboardingPhase =
  | 'welcome'
  | 'offerEveryday'
  | 'summary'
  | 'goals';

interface OnboardingFlowProps {
  user: UserProfile;
  phase: OnboardingPhase;
  onStartTranscription: () => void;
  onStartComposition: () => void;
  onSkipComposition: () => void;
  onContinueToGoals: () => void;
  onCompleteGoals: (archetypeId: GoalArchetypeId) => void;
}

export default function OnboardingFlow({
  user,
  phase,
  onStartTranscription,
  onStartComposition,
  onSkipComposition,
  onContinueToGoals,
  onCompleteGoals,
}: OnboardingFlowProps) {
  const [selectedGoal, setSelectedGoal] = useState<GoalArchetypeId | null>(
    null,
  );

  const suggested = suggestArchetypeFromBaseline(
    user.baselineTranscription?.wpm ?? 0,
  );

  if (phase === 'welcome') {
    return (
      <div className="mx-auto flex w-full max-w-lg flex-col gap-6 text-center">
        <header>
          <p className="label">Velkommen, {user.name}</p>
          <h1 className="mt-1 text-3xl font-semibold text-strong">
            Lad os finde dit udgangspunkt
          </h1>
          <p className="mt-3 text-sm text-muted">
            Først en kort afskriftstest (tekst på skærmen). Derefter kan du
            måle hverdagshastighed med fri skrivning — det de fleste gør mest.
            Til sidst vælger du et mål at arbejde mod.
          </p>
        </header>
        <button
          type="button"
          onClick={onStartTranscription}
          className="w-full rounded-xl bg-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-400"
        >
          Start afskriftstest
        </button>
        <p className="text-xs text-faint">Ca. 1–2 minutter</p>
      </div>
    );
  }

  if (phase === 'offerEveryday') {
    return (
      <div className="mx-auto flex w-full max-w-lg flex-col gap-6 text-center">
        <header>
          <p className="label">Afskrift gemt</p>
          <h1 className="mt-1 text-2xl font-semibold text-strong">
            Vil du måle hverdagshastighed?
          </h1>
          <p className="mt-3 text-sm text-muted">
            Skriv frit i ét minut — uden tekst at kopiere. For mange er det
            tættere på hverdagsskrivning.
          </p>
          {user.baselineTranscription && (
            <p className="mt-2 font-mono text-sm text-strong">
              Afskrift: {user.baselineTranscription.wpm.toFixed(0)} wpm
            </p>
          )}
        </header>
        <button
          type="button"
          onClick={onStartComposition}
          className="w-full rounded-xl bg-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-400"
        >
          Tag hverdagstest
        </button>
        <button
          type="button"
          onClick={onSkipComposition}
          className="text-sm text-muted transition hover:text-strong"
        >
          Spring over
        </button>
      </div>
    );
  }

  if (phase === 'summary') {
    return (
      <BaselineSummaryStep
        transcription={user.baselineTranscription}
        composition={user.baselineComposition}
        onContinue={onContinueToGoals}
        continueLabel="Vælg dit mål"
      />
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-6">
      <header className="text-center">
        <p className="label">Dit mål</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">
          Hvad vil du opnå?
        </h1>
        <p className="mt-2 text-sm text-muted">
          Vælg den beskrivelse der passer bedst. Du kan justere tal senere under
          indstillinger.
        </p>
      </header>
      <GoalArchetypePicker
        suggestedId={suggested}
        selectedId={selectedGoal}
        onSelect={setSelectedGoal}
      />
      <button
        type="button"
        disabled={!selectedGoal}
        onClick={() => selectedGoal && onCompleteGoals(selectedGoal)}
        className="w-full rounded-xl bg-indigo-500 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Afslut og gå til dashboard
      </button>
    </div>
  );
}
